import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { SquarePayment } from "@/components/SquarePayment";
import { RefundControl } from "@/components/RefundControl";
import { useAuth } from "@/context/AuthContext";
import { Loading, StatusChip, FieldRule, Placeholder } from "@/components/common";
import { Lock, Unlock, CheckCircle2, XCircle, DollarSign, ChevronLeft, ShieldCheck, ScanLine, Calculator, Pencil, Trash2, Plus, X, AlertCircle } from "lucide-react";
import { ServiceBuilder } from "@/components/ServiceBuilder";
import { ReasonModal } from "@/components/ReasonModal";
import { toast } from "sonner";

const CORRECTION_AREAS = [
  { key: "services", label: "Purchased services / scope" },
  { key: "quantity", label: "Service quantity (e.g. vehicle count)" },
  { key: "customer", label: "Customer identity" },
  { key: "property", label: "Property / address" },
  { key: "pricing", label: "Pricing / tier" },
  { key: "schedule", label: "Schedule / crew" },
  { key: "other", label: "Other" },
];

const STAGES = [
  "arrival", "property_scan", "estimate", "customer_authorization", "payment",
  "prep", "service", "mid_job_qc", "final_qc", "before_after_photos", "customer_walkthrough", "closeout",
];
const STAGE_LABEL = {
  arrival: "Arrival", property_scan: "Property Scan", estimate: "Estimate",
  customer_authorization: "Customer Authorization", payment: "Payment", prep: "Prep",
  service: "Service", mid_job_qc: "Mid-Job QC", final_qc: "Final QC",
  before_after_photos: "Before/After Photos", customer_walkthrough: "Customer Walkthrough", closeout: "Closeout", closed: "Closed",
};
const QC_CHECKS = [["mid_job", "Mid-Job QC"], ["technician_final", "Technician Final QC"], ["partner", "Partner QC"], ["customer_walkthrough", "Customer Walkthrough"]];
const CLOSEOUT_ITEMS = [["scope_completed", "Scope completed"], ["qc_completed", "QC completed"], ["after_photos", "After photos"], ["property_reset", "Property reset"], ["equipment_accounted", "Equipment accounted"], ["walkthrough", "Customer walkthrough"], ["acknowledged", "Completion acknowledged"]];

export default function JobWorkflow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [payAmt, setPayAmt] = useState("");
  const [totalInput, setTotalInput] = useState("");
  const [totalReason, setTotalReason] = useState("");
  const [deposit, setDeposit] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [crews, setCrews] = useState([]);
  const [addSvcOpen, setAddSvcOpen] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [editProp, setEditProp] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const isOwner = user.role === "owner";
  const isManager = user.role === "manager";
  const authorized = !!(job?.quote && job.quote.multi_service);
  const inScopeEdit = job?.scope_change?.status === "editing";
  const canEdit = isOwner || isManager || (user.role === "technician" && (!authorized || inScopeEdit));

  const load = async () => {
    const { data } = await api.get(`/jobs/${id}`);
    setJob(data);
    if (data.quote?.final_price) setPayAmt(data.quote.final_price);
    try { const dep = await api.get(`/jobs/${id}/deposit-required`); setDeposit(dep.data); } catch {}
    try { const rc = await api.get(`/jobs/${id}/receipt`); setReceipt(rc.data); } catch { setReceipt(null); }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    api.get("/pricing/catalog").then(({ data }) => setCatalog(data));
    api.get("/crews").then(({ data }) => setCrews(data));
  }, []);

  const addService = async (svc) => {
    try { await api.post(`/jobs/${id}/services`, { services: [svc] }); toast.success("Service added"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeService = async (sid) => {
    try { await api.delete(`/jobs/${id}/services/${sid}`); toast.success("Service removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveJobEdit = async () => {
    const { mode, ...body } = editJob;
    try { await api.patch(`/jobs/${id}`, body); toast.success("Job updated"); setEditJob(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const submitCorrection = async (_text, meta) => {
    const area = CORRECTION_AREAS.find((a) => a.key === meta.reason)?.label || meta.reason;
    try { await api.post(`/jobs/${id}/correction-request`, { area, note: meta.note }); toast.success("Correction requested — Manager/Owner notified"); setCorrectOpen(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const savePropEdit = async () => {
    try { await api.patch(`/properties/${job.property_id}`, editProp); toast.success("Property updated"); setEditProp(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const advance = async (stage) => {
    try { await api.patch(`/jobs/${id}/stage`, { stage }); toast.success(`Stage → ${STAGE_LABEL[stage]}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Blocked"); }
  };

  const recordPayment = async (kind) => {
    const amount = kind === "deposit" ? (deposit?.amount || 0) : Number(payAmt) || 0;
    try {
      const { data } = await api.post(`/jobs/${id}/payment`, { kind, amount });
      toast[data.service_locked ? "warning" : "success"](data.service_locked ? "Payment recorded — service still locked" : "JOB AUTHORIZED — BEGIN PREP");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Payment failed"); }
  };

  const setTotal = async () => {
    const fp = Number(totalInput) || 0;
    if (fp <= 0) { toast.error("Enter a total greater than 0"); return; }
    if (!totalReason.trim()) { toast.error("A documented reason is required for a custom price"); return; }
    try {
      await api.patch(`/jobs/${id}/quote`, { final_price: fp, reason: totalReason });
      toast.success("Custom price set (Owner authority)");
      setPayAmt(fp); setTotalReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const requestCustomPricing = async () => {
    const reason = window.prompt("Reason custom pricing is required:", "");
    if (!reason) return;
    const scope = window.prompt("Scope / notes (optional):", "") || "";
    try { await api.post(`/custom-pricing-requests`, { job_id: id, reason, scope }); toast.success("Custom pricing request sent to Owner"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setQC = async (check, passed) => {
    await api.post(`/jobs/${id}/qc`, { check, passed, note: passed ? "" : "CORRECTION REQUIRED" });
    load();
  };

  const setCloseoutItem = async (key, val) => { await api.post(`/jobs/${id}/closeout`, { [key]: val }); load(); };

  const closeJob = async () => {
    try { await api.post(`/jobs/${id}/close`); toast.success("Job closed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot close"); }
  };

  if (!job) return <Loading label="Loading job" />;
  const stageIdx = STAGES.indexOf(job.stage);
  const inServicePhase = ["property_scan", "prep", "service"].includes(job.stage);

  return (
    <div className="max-w-6xl">
      <button className="sg-btn sg-btn-ghost mb-4" onClick={() => navigate(-1)} data-testid="back-btn"><ChevronLeft size={18} /> Back</button>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-head text-3xl font-extrabold">{job.title || "Job"}</h1>
            {canEdit ? (
              <button className="sg-btn" style={{ minHeight: 34, padding: "4px 10px" }} data-testid="edit-job-btn"
                onClick={() => setEditJob({ mode: "full", title: job.title || "", notes: job.notes || "", crew_id: job.crew_id || "", scheduled_date: job.scheduled_date || "", is_test: !!job.is_test })}>
                <Pencil size={14} /> Edit Job
              </button>
            ) : (
              <button className="sg-btn" style={{ minHeight: 34, padding: "4px 10px" }} data-testid="edit-label-btn"
                onClick={() => setEditJob({ mode: "label", title: job.title || "", notes: job.notes || "" })}>
                <Pencil size={14} /> Edit Label
              </button>
            )}
          </div>
          {(job.customer_name || job.address) && (
            <div className="text-sm mt-1 flex items-center gap-2" data-testid="job-identity" style={{ color: "var(--txt2)" }}>
              <span>{job.customer_name}{job.address ? ` · ${job.address}` : ""}{job.crew_number ? ` · ${job.crew_number}` : ""}{job.scheduled_date ? ` · ${job.scheduled_date}` : ""}</span>
              {canEdit && job.property_id && (
                <button className="sg-btn" style={{ minHeight: 28, padding: "2px 8px" }} data-testid="edit-property-btn"
                  onClick={() => setEditProp({ address: job.address || "", notes: "" })}><Pencil size={12} /> Property</button>
              )}
            </div>
          )}
          <div className="text-[11px] font-mono mt-0.5" style={{ color: "var(--muted)" }}>Job {job.id}</div>
          <div className="flex gap-2 mt-2">
            <StatusChip status={job.stage === "closed" ? "ready" : "in_progress"} label={STAGE_LABEL[job.stage]} />
            <StatusChip status={job.payment_state} />
            {job.service_locked ? <StatusChip status="locked" label="Service Locked" /> : <StatusChip status="ready" label="Unlocked" />}
            {job.is_test && <span className="chip" data-testid="job-test-badge" style={{ background: "rgba(245,158,11,0.18)", color: "#F59E0B", borderColor: "#F59E0B", fontWeight: 700 }}>CONTROLLED FIELD TEST</span>}
          </div>
          {(user.role === "owner" || user.role === "manager") && (
            <button className="sg-btn mt-2" style={{ minHeight: 34, padding: "4px 10px" }} data-testid="job-designation-toggle"
              onClick={async () => {
                if (!window.confirm(`Change this job to ${job.is_test ? "LIVE" : "CONTROLLED FIELD TEST"}?`)) return;
                try { await api.patch(`/jobs/${id}/designation`, { is_test: !job.is_test }); toast.success(`Job marked ${job.is_test ? "LIVE" : "TEST"}`); load(); }
                catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}>Mark as {job.is_test ? "Live" : "Field Test"}</button>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Collected</div>
          <div className="font-head text-3xl font-extrabold" style={{ color: "var(--accent)" }}>${(job.collected_revenue || 0).toLocaleString()}</div>
        </div>
      </div>

      {/* Stepper */}
      <div className="sg-card p-4 mb-6 overflow-x-auto" data-testid="job-stepper">
        <div className="flex gap-2 min-w-max">
          {STAGES.map((s, i) => (
            <div key={s} className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap ${i <= stageIdx ? "text-white" : ""}`}
              style={{ background: i < stageIdx ? "#10B981" : i === stageIdx ? "var(--accent)" : "var(--bg-elev)", color: i <= stageIdx ? "#fff" : "var(--txt2)" }}>
              {i + 1}. {STAGE_LABEL[s]}
            </div>
          ))}
        </div>
      </div>

      {/* In-context job workspace links */}
      <div className="sg-card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="workspace-links">
        <span className="font-head text-sm uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Job Workspace</span>
        <button className="sg-btn" style={{ minHeight: 44 }} onClick={() => navigate(`/shadow-scan?job=${id}`)} data-testid="open-scan"><ScanLine size={16} /> Shadow Scan</button>
        <button className="sg-btn" style={{ minHeight: 44 }} onClick={() => navigate(`/estimator?job=${id}`)} data-testid="open-estimator"><Calculator size={16} /> Estimator</button>
        {job.scan && <StatusChip status="ready" label={`Scan: ${job.scan.condition}/${job.scan.access}/${job.scan.specialty}`} />}
      </div>

      {/* Structured Services — single source of truth for job scope */}
      <div className="sg-card p-5 mb-6" data-testid="job-services-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="font-head text-lg font-bold">Services on this Job</div>
          {canEdit ? (
            <button className="sg-btn" style={{ minHeight: 38 }} onClick={() => setAddSvcOpen((v) => !v)} data-testid="toggle-add-service">
              <Plus size={16} /> Add Service
            </button>
          ) : (
            <button className="sg-btn" style={{ minHeight: 38 }} onClick={() => setCorrectOpen(true)} data-testid="request-correction-btn">
              <AlertCircle size={16} /> Request Job Correction
            </button>
          )}
        </div>
        {!canEdit && (
          <div className="text-xs mb-3 p-2 rounded-lg" style={{ background: "rgba(59,130,246,0.08)", color: "var(--txt2)" }} data-testid="tech-scope-note">
            Purchased services, quantities, customer, property, schedule/crew and pricing are locked. To change any of these, use Request Job Correction.
          </div>
        )}
        {canEdit && addSvcOpen && catalog && (
          <div className="sg-card-elev p-4 mb-3" data-testid="add-service-inline">
            <ServiceBuilder catalog={catalog} onAdd={(s) => { addService(s); setAddSvcOpen(false); }} />
          </div>
        )}
        {(!job.job_services || job.job_services.length === 0) ? (
          <div className="text-sm p-3 rounded-lg" style={{ background: "rgba(245,158,11,0.1)", color: "#F59E0B" }} data-testid="no-job-services">
            No services yet. Add services above — scope must come from structured services, not the title.
          </div>
        ) : (
          <div className="flex flex-col gap-2" data-testid="job-services-list">
            {job.job_services.map((s) => (
              <div key={s.job_service_id} className="flex items-center justify-between sg-card-elev p-3" data-testid={`job-service-${s.job_service_id}`}>
                <div>
                  <div className="font-semibold text-sm">{s.label}{s.quantity_index ? ` (Vehicle ${s.quantity_index})` : ""}
                    <span className="text-xs ml-2" style={{ color: "var(--txt2)" }}>{s.size_key || (s.measure ? `${s.measure} ft` : "")}</span>
                  </div>
                  {s.status === "priced"
                    ? <div className="text-xs mt-0.5" style={{ color: "var(--accent)" }}>{s.tier} · ${Number((s.quote || {}).final_price || 0).toLocaleString()}</div>
                    : <div className="text-xs mt-0.5" style={{ color: "#F59E0B" }}>No tier — set in Estimator</div>}
                </div>
                <button onClick={() => removeService(s.job_service_id)} disabled={!canEdit} style={{ opacity: canEdit ? 1 : 0.3, cursor: canEdit ? "pointer" : "not-allowed" }} data-testid={`remove-job-service-${s.job_service_id}`}><Trash2 size={16} style={{ color: "var(--danger)" }} /></button>
              </div>
            ))}
            <button className="sg-btn sg-btn-accent mt-2" style={{ alignSelf: "flex-start" }} onClick={() => navigate(`/estimator?job=${id}`)} data-testid="services-to-estimator">
              <Calculator size={16} /> Price in Estimator
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 flex flex-col gap-6">
          {/* Payment Gate */}
          <div className="sg-card p-5" data-testid="payment-gate">
            <div className="font-head text-xl font-bold mb-3 flex items-center gap-2">
              {job.service_locked ? <Lock size={20} style={{ color: "var(--danger)" }} /> : <Unlock size={20} style={{ color: "#10B981" }} />} Payment Gate
            </div>
            <div className="p-3 rounded-lg mb-4 font-head font-bold text-center"
              style={{ background: job.service_locked ? "rgba(239,68,68,0.12)" : "rgba(16,185,129,0.12)", color: job.service_locked ? "#EF4444" : "#10B981" }}
              data-testid="gate-status">
              {job.service_locked ? "SERVICE LOCKED — NO WORK UNTIL PAYMENT CLEARS" : "JOB AUTHORIZED — BEGIN PREP"}
            </div>
            {deposit && <div className="text-sm mb-3" style={{ color: "var(--txt2)" }}>Deposit rule: {deposit.note} {deposit.amount > 0 && <b>(${deposit.amount})</b>}</div>}
            {job.quote?.selected_tier && (
              <div className="text-sm mb-3" data-testid="selected-tier">Authorized tier: <b style={{ color: "var(--accent)" }}>{job.quote.selected_tier_label || job.quote.selected_tier}</b></div>
            )}
            {job.quote?.custom && <div className="text-xs mb-2" data-testid="custom-price-flag" style={{ color: "#F59E0B" }}>Custom price set by {job.quote.custom_by} — {job.quote.custom_reason}</div>}
            {!job.quote?.final_price ? (
              <div className="mb-2" data-testid="set-total-block">
                {isOwner ? (
                  <>
                    <div className="text-sm mb-2" style={{ color: "var(--warn)" }}>No authorized total. Normally set via Estimator tier selection. Owner may set a custom price here (exception, audited).</div>
                    <div className="flex flex-wrap gap-2">
                      <input className="sg-input" style={{ width: 140 }} type="number" value={totalInput} onChange={(e) => setTotalInput(e.target.value)} data-testid="job-total-input" placeholder="Custom $" />
                      <input className="sg-input" style={{ minWidth: 200, flex: 1 }} value={totalReason} onChange={(e) => setTotalReason(e.target.value)} data-testid="job-total-reason" placeholder="Reason (required)" />
                      <button className="sg-btn" onClick={setTotal} data-testid="set-total-btn">Set Custom Price</button>
                    </div>
                  </>
                ) : (
                  <div data-testid="no-total-tech">
                    <div className="text-sm mb-2" style={{ color: "var(--warn)" }}>No authorized total yet. Pricing comes from Shadow Scan → Estimator. Open the Estimator and have the customer select a tier.</div>
                    <div className="flex gap-2">
                      <button className="sg-btn" onClick={() => navigate(`/estimator?job=${id}`)} data-testid="go-estimator-btn"><Calculator size={16} /> Open Estimator</button>
                      {isManager && <button className="sg-btn" onClick={requestCustomPricing} data-testid="request-custom-pricing-btn">Request Custom Pricing</button>}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                {job.dispute_state && (
                  <div className="mb-3 p-3 rounded-lg" style={{ background: "rgba(239,68,68,0.12)", color: "#EF4444", border: "1px solid #EF4444" }} data-testid="job-dispute-banner">
                    <b>Payment disputed / chargeback.</b> ${Number(job.dispute_amount || 0).toLocaleString()} is under dispute and has been removed from collected revenue. Square is authoritative — respond in Square before the deadline.
                  </div>
                )}
                <div className="text-sm mb-3" style={{ color: "var(--txt2)" }}>
                  Total due: <b style={{ color: "var(--txt)" }}>${Number(job.quote.final_price).toLocaleString()}</b> · Collected ${Number(job.collected_revenue || 0).toLocaleString()}
                </div>
                {job.payment_state !== "paid" && (
                  <SquarePayment
                    jobId={id}
                    total={Number(job.quote.final_price)}
                    collected={Number(job.collected_revenue || 0)}
                    amountLabel={`$${Math.max(0, Number(job.quote.final_price) - Number(job.collected_revenue || 0)).toLocaleString()}`}
                    onPaid={load}
                  />
                )}
                <div className="flex flex-wrap gap-2 items-center">
                  <button className="sg-btn" onClick={() => recordPayment("deposit")} data-testid="record-deposit"><DollarSign size={16} /> Record Deposit</button>
                  <button className="sg-btn sg-btn-accent" onClick={() => recordPayment("full")} data-testid="record-payment"><DollarSign size={16} /> Record Full Payment (Manual)</button>
                  {isManager && <button className="sg-btn" onClick={requestCustomPricing} data-testid="request-custom-pricing-btn">Request Custom Pricing</button>}
                </div>
              </>
            )}
            <div className="text-xs mt-3" style={{ color: "var(--muted)" }}>
              Card payments run through Square (Sandbox). Manual recording remains for cash/check or offline settlement. Job Mode unlocks only when payment clears.
            </div>
            {receipt?.latest && (
              <div className="sg-card-elev p-3 mt-3 flex items-center justify-between gap-2" data-testid="job-receipt">
                <div className="text-sm" style={{ color: "var(--txt2)" }}>Square receipt available for this job</div>
                <button className="sg-btn" style={{ minHeight: 36 }} onClick={() => window.open(receipt.latest, "_blank", "noopener")} data-testid="view-receipt-btn">View / Resend Receipt</button>
              </div>
            )}
            {(isOwner || isManager) && <RefundControl jobId={id} onRefunded={load} />}
          </div>

          {/* Field rule during service phases */}
          {inServicePhase && <FieldRule />}

          {/* QC */}
          <div className="sg-card p-5" data-testid="qc-panel">
            <div className="font-head text-xl font-bold mb-3">Quality Control</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {QC_CHECKS.map(([key, label]) => (
                <div key={key} className="sg-card-elev p-3 flex items-center justify-between" data-testid={`qc-${key}`}>
                  <div>
                    <div className="font-semibold text-sm">{label}</div>
                    <StatusChip status={job.qc?.[key] ? "ready" : "pending"} label={job.qc?.[key] ? "Passed" : "Pending"} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setQC(key, true)} data-testid={`qc-pass-${key}`}><CheckCircle2 size={22} style={{ color: "#10B981" }} /></button>
                    <button onClick={() => setQC(key, false)} data-testid={`qc-fail-${key}`}><XCircle size={22} style={{ color: "#EF4444" }} /></button>
                  </div>
                </div>
              ))}
            </div>
            {job.qc?.corrections?.filter((c) => c.status === "open").length > 0 && (
              <div className="mt-3 p-3 rounded-lg" style={{ background: "rgba(239,68,68,0.1)", color: "#EF4444" }} data-testid="corrections">
                CORRECTION REQUIRED — {job.qc.corrections.filter((c) => c.status === "open").length} open. Closeout blocked until resolved.
              </div>
            )}
          </div>

          {/* Photos placeholder for camera alignment */}
          <div className="sg-card p-5" data-testid="photos-panel">
            <div className="font-head text-xl font-bold mb-2">Before / After Photos</div>
            <p className="text-sm mb-3" style={{ color: "var(--txt2)" }}>Capture via Shadow Scan & service. Camera ghost-alignment is a future computer-vision feature.</p>
            <Placeholder label="Camera Alignment — Future Phase" />
          </div>

          {/* Closeout */}
          <div className="sg-card p-5" data-testid="closeout-panel">
            <div className="font-head text-xl font-bold mb-3">Job Closeout</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CLOSEOUT_ITEMS.map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm sg-card-elev p-3" data-testid={`closeout-${key}`}>
                  <input type="checkbox" checked={!!job.closeout?.[key]} onChange={(e) => setCloseoutItem(key, e.target.checked)} /> {label}
                </label>
              ))}
            </div>
            <button className="sg-btn sg-btn-accent w-full mt-4" onClick={closeJob} disabled={job.stage === "closed"} data-testid="close-job-btn">
              <ShieldCheck size={18} /> {job.stage === "closed" ? "Job Closed" : "Close Job"}
            </button>
          </div>
        </div>

        {/* Stage controls */}
        <div className="flex flex-col gap-4">
          <div className="sg-card p-5 sticky top-24" data-testid="stage-controls">
            <div className="font-head text-lg font-bold mb-3">Workflow</div>
            <div className="flex flex-col gap-2">
              {STAGES.map((s, i) => (
                <button key={s} onClick={() => advance(s)} disabled={job.stage === "closed"}
                  className={`sg-btn justify-start ${i === stageIdx ? "sg-btn-accent" : ""}`} data-testid={`stage-${s}`}>
                  {i < stageIdx ? <CheckCircle2 size={16} style={{ color: "#10B981" }} /> : <span className="font-mono text-xs">{i + 1}</span>}
                  {STAGE_LABEL[s]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {editJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setEditJob(null)}>
          <div className="sg-card p-6 w-full max-w-md sg-fade" style={{ maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()} data-testid="edit-job-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-head text-2xl font-bold">{editJob.mode === "label" ? "Edit Label" : "Edit Job"}</h3><button onClick={() => setEditJob(null)}><X size={22} /></button></div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Title (label only)</label>
            <input className="sg-input mt-1 mb-3" value={editJob.title} data-testid="edit-job-title" onChange={(e) => setEditJob((j) => ({ ...j, title: e.target.value }))} />
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Notes</label>
            <input className="sg-input mt-1 mb-3" value={editJob.notes} data-testid="edit-job-notes" onChange={(e) => setEditJob((j) => ({ ...j, notes: e.target.value }))} />
            {editJob.mode === "full" ? (
              <>
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Crew</label>
                <select className="sg-input mt-1 mb-3" value={editJob.crew_id} data-testid="edit-job-crew" onChange={(e) => setEditJob((j) => ({ ...j, crew_id: e.target.value }))}>
                  {crews.map((c) => <option key={c.id} value={c.id}>{c.crew_number}</option>)}
                </select>
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Scheduled date</label>
                <input className="sg-input mt-1 mb-3" type="date" value={editJob.scheduled_date} data-testid="edit-job-date" onChange={(e) => setEditJob((j) => ({ ...j, scheduled_date: e.target.value }))} />
                <label className="flex items-center gap-2 text-sm mb-4" style={{ color: editJob.is_test ? "#F59E0B" : "var(--txt)" }}>
                  <input type="checkbox" checked={editJob.is_test} data-testid="edit-job-istest" onChange={(e) => setEditJob((j) => ({ ...j, is_test: e.target.checked }))} /> Controlled Field Test job (excluded from live metrics)
                </label>
                <div className="text-xs mb-3 p-2 rounded-lg" style={{ background: "rgba(59,130,246,0.08)", color: "var(--txt2)" }}>Services & quantities are managed in the Services panel above (same job — no duplicate record).</div>
              </>
            ) : (
              <div className="text-xs mb-4 p-2 rounded-lg" style={{ background: "rgba(59,130,246,0.08)", color: "var(--txt2)" }}>You can fix the label/notes only. For services, quantity, customer, property, schedule or pricing, use Request Job Correction.</div>
            )}
            <button className="sg-btn sg-btn-accent w-full" onClick={saveJobEdit} data-testid="save-job-edit">Save Changes</button>
          </div>
        </div>
      )}

      {correctOpen && (
        <ReasonModal title="Request Job Correction"
          subtitle="What needs correcting? A Manager/Owner will make the protected change."
          options={CORRECTION_AREAS} requireNoteFor={["other"]} confirmLabel="Send Request"
          onConfirm={submitCorrection} onClose={() => setCorrectOpen(false)} testid="correction-modal" />
      )}

      {editProp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setEditProp(null)}>
          <div className="sg-card p-6 w-full max-w-md sg-fade" onClick={(e) => e.stopPropagation()} data-testid="edit-property-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-head text-2xl font-bold">Edit Property</h3><button onClick={() => setEditProp(null)}><X size={22} /></button></div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Address</label>
            <input className="sg-input mt-1 mb-3" value={editProp.address} data-testid="edit-prop-address" onChange={(e) => setEditProp((p) => ({ ...p, address: e.target.value }))} />
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Notes</label>
            <input className="sg-input mt-1 mb-4" value={editProp.notes} data-testid="edit-prop-notes" onChange={(e) => setEditProp((p) => ({ ...p, notes: e.target.value }))} />
            <button className="sg-btn sg-btn-accent w-full" onClick={savePropEdit} data-testid="save-prop-edit">Save Changes</button>
          </div>
        </div>
      )}
    </div>
  );
}
